import { IRestClient } from "@/core/interfaces/restClient.interface";
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { ICoreResponse } from "@/core/interfaces/coreResponse.interface";
import { StatusCodes } from "http-status-codes";
import storageService from "@/core/services/storage.service";
import { STORAGE_KEYS } from "@/core/constants/storageKeys";
import { API_ENDPOINTS } from "@/core/configs/apiEndpoints";
import { AUTH_EVENTS, authBus } from "@/core/eventBusses/auth";
import UTILS from "../utils";

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

class RestClientService implements IRestClient {
  private readonly axios: AxiosInstance;
  private storageService = storageService;
  private pxResponse: ICoreResponse<AxiosResponse> = {
    success: false,
    error: null,
    errors: null,
  };
  private whiteListRoutes = [
    API_ENDPOINTS.LOGIN_ENDPOINT,
    API_ENDPOINTS.FORGOT_PASSWORD_ENDPOINT,
    API_ENDPOINTS.REFRESH_TOKEN_ENDPOINT,
    API_ENDPOINTS.DISABLE_MFA_ENDPOINT,
  ];
  private DEFAULT_ERROR_MESSAGE = "An error occurred";

  constructor() {
    this.axios = axios.create({
      baseURL: import.meta.env.VITE_APP_API_ENDPOINT,
      headers: {
        "Content-Type": "application/json",
      },
      timeout: 180000,
    });
    this.axios.interceptors.request.use((req: InternalAxiosRequestConfig) => {
      const token = this.storageService.getItem<string>(
        STORAGE_KEYS.ACCESS_TOKEN,
        false,
      );

      if (token && req.headers) {
        req.headers["Authorization"] = `Bearer ${token}`;
      }
      return req;
    });
    this.axios.interceptors.response.use(
      (response: AxiosResponse): any => {
        return this.handleResponse(response);
      },
      (err: AxiosError) => {
        return this.handleError(err);
      },
    );
  }

  public async get(
    endpoint: string,
    params?: any,
    additionalConfig?: AxiosRequestConfig,
  ): Promise<ICoreResponse> {
    additionalConfig ||= {};
    return this.axios.get(endpoint, { params, ...additionalConfig });
  }

  public async post(
    endpoint: string,
    body: any,
    additionalConfig?: AxiosRequestConfig,
  ): Promise<ICoreResponse> {
    return this.axios.post(endpoint, body, additionalConfig);
  }

  public async put(
    endpoint: string,
    body?: any,
    additionalConfig?: AxiosRequestConfig,
  ): Promise<ICoreResponse> {
    return this.axios.put(endpoint, body, additionalConfig);
  }

  public async patch(
    endpoint: string,
    body: any,
    params?: any,
    additionalConfig?: AxiosRequestConfig,
  ): Promise<ICoreResponse> {
    return this.axios.patch(endpoint, body, {
      params: params || {},
      ...additionalConfig,
    });
  }

  public async getById(
    endpoint: string,
    id: string,
    additionalConfig?: AxiosRequestConfig,
  ): Promise<ICoreResponse> {
    return this.axios.get(`${endpoint}/${id}`, additionalConfig);
  }

  public async delete(
    endpoint: string,
    id: string,
    additionalConfig?: AxiosRequestConfig,
  ): Promise<ICoreResponse> {
    return this.axios.delete(`${endpoint}/${id}`, additionalConfig);
  }

  private handleResponse(response: AxiosResponse) {
    this.pxResponse = {
      status: response.status,
      data: response?.data?.response || response?.data,
      message: response?.data?.message || "",
      success:
        typeof response?.data?.success === "boolean"
          ? response?.data?.success
          : true,
      error: null,
    };
    return this.pxResponse;
  }

  private isEmptyObject(obj: any): boolean {
    return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
  }

  private isObject(data: object): boolean {
    return data && data.constructor === Object;
  }

  private isArray(data: string[]): boolean {
    return Array.isArray(data);
  }

  private parseObjectToString(data: object): string | null {
    return !this.isEmptyObject(data) ? JSON.stringify(data) : null;
  }

  private parseArrayToString(data: string[]): string | null {
    return data.length > 0 ? data.join(", ") : null;
  }

  private parseErrorToString(data: any | null): string | null {
    if (this.isObject(data)) {
      return this.parseObjectToString(data);
    } else if (this.isArray(data)) {
      return this.parseArrayToString(data);
    } else {
      return data;
    }
  }

  private async handleError(error: AxiosError) {
    if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
      return {
        success: false,
        aborted: true,
        error: null,
        errors: null,
        message: "",
      };
    }

    const errorConfig = error.config as CustomAxiosRequestConfig;

    let message = "";
    let pxResponseMessage = "";
    let errorMessage: string | null = null;
    let errorsMessage: string | null = null;
    let status = StatusCodes.INTERNAL_SERVER_ERROR;
    let bookingErrors = null;

    if (error.response) {
      const errorData = error.response.data as ICoreResponse<AxiosResponse>;

      message = errorData.message ?? this.DEFAULT_ERROR_MESSAGE;
      status = errorData.status ?? StatusCodes.INTERNAL_SERVER_ERROR;

      if (
        errorConfig &&
        !this.whiteListRoutes.includes(errorConfig.url ?? "")
      ) {
        if (
          error.response.status === StatusCodes.UNAUTHORIZED &&
          !errorConfig._retry
        ) {
          errorConfig._retry = true;

          return this.refreshTokenCall(errorConfig);
        } else if (error.response.status === StatusCodes.FORBIDDEN) {
          authBus.emit(AUTH_EVENTS.ROUTE_ON_ERROR_403, location.pathname);
        }
      }

      bookingErrors = errorData.errors;
      errorMessage = this.parseErrorToString(errorData.error);
      errorsMessage = this.parseErrorToString(errorData.errors);
    }

    if (message.trim().length >= 0) {
      pxResponseMessage = message;
    } else {
      pxResponseMessage =
        errorMessage || errorsMessage || this.DEFAULT_ERROR_MESSAGE;
    }

    this.pxResponse = {
      error: errorMessage,
      errors: bookingErrors,
      status,
      success: false,
      message: pxResponseMessage,
    };

    return Promise.resolve(this.pxResponse);
  }

  private async refreshTokenCall(
    originalRequestConfig: CustomAxiosRequestConfig,
  ) {
    try {
      const refreshTokenApiResponse: ICoreResponse = await this.post(
        API_ENDPOINTS.REFRESH_TOKEN_ENDPOINT,
        {
          refresh: this.storageService.getItem(
            STORAGE_KEYS.REFRESH_TOKEN,
            false,
          ),
        },
      );
      const { access, refresh } = refreshTokenApiResponse.data;

      UTILS.updateAuthTokens(access, refresh);

      return this.axios(originalRequestConfig);
    } catch (error) {
      console.error("Token refresh failed:", error);
      authBus.emit(AUTH_EVENTS.LOGOUT);
    }
  }
}

export default RestClientService;
